import React, { Suspense, useEffect } from 'react';
import { useCart } from './hooks/useCart';
import PortalLoader from './components/PortalLoader';
import CONSTANTS from './_constants';
import { Toast } from './components/portals/Toast';
import { SearchProvider } from './context/search/useSearch';


const LoadingBar = React.lazy(() => import('./components/portals/LoadingBar'));
const CartCountBadge = React.lazy(() => import('./components/portals/CartCountBadge'));
const ProductRecommendations = React.lazy(() => import('./components/portals/ProductRecommendations'));
const CartRecommendations = React.lazy(() => import('./components/portals/CartRecommendations'));
const MiniCart = React.lazy(() => import('./components/portals/MiniCart/index'));
const QuickAdd = React.lazy(() => import('./components/portals/QuickAdd/index'));
const SearchInput = React.lazy(() => import('./components/portals/Search/SearchInput'));
const SearchResultsTrending = React.lazy(() => import('./components/portals/Search/SearchResultsTrending'));
const SearchResultsSuggestions = React.lazy(() => import('./components/portals/Search/SearchResultsSuggestions'));
const SearchResultsCollections = React.lazy(() => import('./components/portals/Search/SearchResultsCollections'));
const SearchResultsProducts = React.lazy(() => import('./components/portals/Search/SearchResultsProducts'));
const SearchFilters = React.lazy(() => import('./components/portals/Search/SearchFilters'));
const SearchFilteredProducts = React.lazy(() => import('./components/portals/Search/SearchFilteredProducts'));
const SearchSubmit = React.lazy(() => import('./components/portals/Search/SearchSubmit'));
const SearchFilterCount = React.lazy(() => import('./components/portals/Search/SearchFilterCount'));
const SearchProductsCount = React.lazy(() => import('./components/portals/Search/SearchProductsCount'));
const SearchSortBy = React.lazy(() => import('./components/portals/Search/SearchSortBy'));
const SearchResultsPages = React.lazy(() => import('./components/portals/Search/SearchResultsPages'));
const SearchFilterQuickRemoves = React.lazy(() => import('./components/portals/Search/SearchFilterQuickRemoves'));
const CountdownTimer = React.lazy(() => import('./components/portals/CountdownTimer'));
const FourThreeWidget = React.lazy(() => import('./components/portals/FourThreeWidget'));


type AppProps = {
  config: any,
}

export const App: React.FC<AppProps> = ({ config }) => {
  const cartHelpers = useCart();
  useEffect(() => {
    cartHelpers.getCartState();
    // Ensure blubolt.minicart is set up for header icon click
    if (typeof cartHelpers.setDOMHelpers === 'function') {
      cartHelpers.setDOMHelpers();
    }
  }, []);
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div>
        <PortalLoader dataAttr={CONSTANTS.PORTALS.LOADING} Component={LoadingBar} />
        <PortalLoader dataAttr={CONSTANTS.PORTALS.TOAST} Component={Toast} />
        <PortalLoader dataAttr={CONSTANTS.PORTALS.CART_COUNT_BADGE} Component={CartCountBadge} />
        <PortalLoader dataAttr={CONSTANTS.PORTALS.CART_COUNT_BADGE_2} Component={CartCountBadge} />
        <PortalLoader dataAttr={CONSTANTS.PORTALS.PRODUCT_RECOMMENDATIONS} Component={ProductRecommendations} />
        <PortalLoader dataAttr={CONSTANTS.PORTALS.CART_RECOMMENDATIONS} Component={CartRecommendations} />
        <PortalLoader dataAttr={CONSTANTS.PORTALS.MINICART} Component={MiniCart} />
        <PortalLoader dataAttr={CONSTANTS.PORTALS.QUICKADD} Component={QuickAdd} />
        { config.appLayerConfigSearch
          && config.appLayerConfigSearchProductsPerPage
          && config.appLayerConfigSearchLoadMoreMode ? (
            <SearchProvider
              config={{ ...config.appLayerConfigSearch, rProductsPerPage: config.appLayerConfigSearchProductsPerPage, rLoadMoreMode: config.appLayerConfigSearchLoadMoreMode, searchFilterDummyTag: config.appLayerConfigSearchFilterDummyTag}}
              activeCollection={{
                id: config.appLayerConfigActiveCollectionId ?? null,
                title: config.appLayerConfigActiveCollectionTitle ?? null,
                handle: config.appLayerConfigActiveCollectionHandle ?? null,
              }}>
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_INPUT} Component={SearchInput} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_SUBMIT} Component={SearchSubmit} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_TRENDING} Component={SearchResultsTrending} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_SUGGESTIONS} Component={SearchResultsSuggestions} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_COLLECTIONS} Component={SearchResultsCollections} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_PAGES} Component={SearchResultsPages} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_PRODUCTS} Component={SearchResultsProducts} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_FILTER_QUICK_REMOVES} Component={SearchFilterQuickRemoves} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_FILTERS} Component={SearchFilters} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_FILTERED_PRODUCTS} Component={SearchFilteredProducts} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_PRODUCTS_COUNT} Component={SearchProductsCount} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_FILTER_COUNT} Component={SearchFilterCount} />
              <PortalLoader dataAttr={CONSTANTS.PORTALS.SEARCH_SORT_BY} Component={SearchSortBy} />
            </SearchProvider>
          ) : null }
        <PortalLoader dataAttr={CONSTANTS.PORTALS.COUNTDOWN_TIMER} Component={CountdownTimer} />
        <PortalLoader dataAttr={CONSTANTS.PORTALS.FOUR_THREE_WIDGET} Component={FourThreeWidget} />
      </div>
    </Suspense>
  );
}
export default App;
